import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  ButtonGroup,
  Collapse,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material";
import type { FC } from "react";
import { Controller, useForm } from "react-hook-form";
import { useAsyncStateCallback } from "../../../../../../utils/hooks/use-async-state-callback";
import { LoadingMessage } from "../loading-message";
import type { QueryFn } from "../use-co-pilot-types";
import { type CoPilotSchema, type CoPilotTone, coPilotSchema } from "./schema";

export const InitialPromptForm: FC<{
  onSubmit?: QueryFn;
  tone?: CoPilotTone;
}> = ({ onSubmit = () => Promise.resolve(undefined), tone = "professional" }) => {
  const { handleSubmit, control, watch } = useForm<CoPilotSchema>({
    defaultValues: { prompt: "", tone },
    resolver: yupResolver(coPilotSchema),
  });

  // eslint-disable-next-line react-hooks/incompatible-library
  const prompt = watch("prompt");

  const { load: submit, loading } = useAsyncStateCallback(onSubmit, []);

  return (
    <Stack direction="column" spacing={1}>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id="co-pilot-form" onSubmit={handleSubmit(submit)}>
        <Controller
          name="prompt"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              multiline
              id="prompt"
              type="text"
              fullWidth
              maxRows={10}
              placeholder="Ask CoPilot to..."
              value={field.value}
              onChange={field.onChange}
              helperText={error?.message}
              label="Ask CoPilot to..."
              error={Boolean(error)}
            />
          )}
        />
        <Collapse in={prompt !== ""}>
          <Controller
            name="tone"
            control={control}
            render={({ field }) => (
              <FormControl>
                <RadioGroup
                  row
                  aria-labelledby="co-pilot-tone"
                  name="co-pilot-tone"
                  value={field.value}
                  onChange={field.onChange}
                >
                  <FormControlLabel value="professional" control={<Radio />} label="Professional" />
                  <FormControlLabel value="casual" control={<Radio />} label="Casual" />
                  <FormControlLabel value="direct" control={<Radio />} label="Direct" />
                </RadioGroup>
              </FormControl>
            )}
          />
        </Collapse>

        {loading ? (
          <LoadingMessage loading={true} />
        ) : (
          <ButtonGroup sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button
              data-analytics-id="lexical-co-pilot-generate"
              disabled={prompt === ""}
              type="submit"
              variant="outlined"
              form="co-pilot-form"
            >
              Generate
            </Button>
          </ButtonGroup>
        )}
      </form>
    </Stack>
  );
};
